/**
 * QuestionAdminItem.js
 * Individual question item component for quiz administration
 * Handles rendering of question type selector, drag handle, remove button,
 * and appropriate question input component based on type
 */

import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import {
  Box,
  Flex,
  Select,
  Spacer,
  IconButton,
  Collapse,
  useColorModeValue,
} from '@chakra-ui/react';

import {
  DragHandleIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  CloseIcon,
} from '@chakra-ui/icons';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import EssayInput from 'components/question/inputs/EssayInput';
import MultipleChoiceInput from 'components/question/inputs/MultipleChoiceInput';

import {
  ESSAY_ID,
  MULTIPLE_CHOICE_ID,
  QUESTION_TYPES,
  TRUE_OR_FALSE_ID,
} from 'utils/constants/question';

import { CREATE_MODE, UPDATE_MODE } from 'utils/constants/operationMode';

/**
 * QuestionAdminItem component
 * @param {Object} question - The question object
 * @param {number} index - The question index in the array
 * @param {string} mode - CREATE_MODE or UPDATE_MODE
 * @param {Function} onQuestionTypeChange - Handler for question type changes
 * @param {Function} onRemove - Handler for removing the question
 * @param {Function} onUpdate - Handler for updating the question
 * @param {boolean} isToggled - Controls collapse state (optional, defaults to true)
 * @param {Function} onToggle - Handler for toggle state changes (optional)
 * @param {boolean} forceCollapse - External trigger to force collapse all questions
 * @param {boolean} forceExpand - External trigger to force expand all questions
 */
function QuestionAdminItem({
  question,
  index,
  mode,
  onQuestionTypeChange,
  onRemove,
  onUpdate,
  isToggled: externalIsToggled,
  onToggle,
  forceCollapse,
  forceExpand,
}) {
  const [internalIsToggled, setInternalIsToggled] = useState(true);
  const isToggled =
    externalIsToggled !== undefined ? externalIsToggled : internalIsToggled;

  useEffect(() => {
    // Only apply force actions when using internal state management
    if (externalIsToggled === undefined) {
      if (forceCollapse === true) {
        setInternalIsToggled(false);
      } else if (forceExpand === true) {
        setInternalIsToggled(true);
      }
    }
  }, [forceCollapse, forceExpand, externalIsToggled]);

  const handleToggle = () => {
    if (onToggle) {
      onToggle(index);
    } else {
      setInternalIsToggled((prev) => !prev);
    }
  };

  // Filter question types for CREATE and UPDATE mode (excludes True/False)
  const availableQuestionTypes =
    mode === CREATE_MODE || mode === UPDATE_MODE
      ? QUESTION_TYPES.filter((q) => q.Id !== TRUE_OR_FALSE_ID)
      : QUESTION_TYPES;

  const sortableId = question.tempId;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: sortableId });

  const headerBgColor = useColorModeValue('#f7f7f7', 'gray.800');

  // Apply CSS transforms for drag-and-drop visual feedback
  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Box mb={6} borderWidth="1px" borderRadius="md">
        {/* Header section with controls */}
        <Box w="100%" p={4} bg={headerBgColor}>
          <Flex align="center">
            <IconButton
              {...attributes}
              {...listeners}
              variant="plain"
              color="teal"
              aria-label="Re-order question"
              icon={<DragHandleIcon />}
              size="sm"
              mr={2}
            />

            {/* Toggle button */}
            {isToggled ? (
              <IconButton
                onClick={handleToggle}
                variant="plain"
                aria-label="Collapse question"
                icon={<ArrowUpIcon />}
                size="sm"
                mr={2}
              />
            ) : (
              <IconButton
                onClick={handleToggle}
                variant="plain"
                aria-label="Expand question"
                icon={<ArrowDownIcon />}
                size="sm"
                mr={2}
              />
            )}

            {/* Question type selector */}
            <Box w="40%" mr={4}>
              <Select
                value={question.questionTypeId}
                onChange={(e) =>
                  onQuestionTypeChange(index, question, e.target.value)
                }
                size="sm"
              >
                {availableQuestionTypes.map((type) => (
                  <option value={type.Id} key={type.Id}>
                    {type.Name}
                  </option>
                ))}
              </Select>
            </Box>

            <Spacer />

            {/* Remove button */}
            <IconButton
              isRound
              size="xs"
              icon={<CloseIcon />}
              onClick={() => onRemove(index)}
              colorScheme="red"
              variant="ghost"
            />
          </Flex>
        </Box>

        {/* Collapsible content */}
        <Collapse in={isToggled}>
          <Box p={4}>
            {question.questionTypeId === MULTIPLE_CHOICE_ID && (
              <MultipleChoiceInput
                question={question}
                errors={mode === UPDATE_MODE ? question.errors : null}
                onUpdate={(updatedQuestion) => onUpdate(index, updatedQuestion)}
              />
            )}

            {question.questionTypeId === ESSAY_ID && (
              <EssayInput
                question={question}
                errors={mode === UPDATE_MODE ? question.errors : null}
                onUpdate={(updatedQuestion) => onUpdate(index, updatedQuestion)}
              />
            )}
          </Box>
        </Collapse>
      </Box>
    </div>
  );
}

QuestionAdminItem.defaultProps = {
  isToggled: undefined,
  onToggle: undefined,
  forceCollapse: false,
  forceExpand: false,
};

QuestionAdminItem.propTypes = {
  question: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  mode: PropTypes.string.isRequired,
  onQuestionTypeChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  isToggled: PropTypes.bool,
  onToggle: PropTypes.func,
  forceCollapse: PropTypes.bool,
  forceExpand: PropTypes.bool,
};

export default QuestionAdminItem;
